import React, { useState, useEffect, useRef } from 'react'
import { ApprovalRouteVersionService } from '../../services/approvalRouteVersionService'
import { UserService, type UserSearchResult } from '../../services/userService'
import type {
  ApprovalRouteDetail,
  ApprovalRouteVersion,
  CreateApprovalRouteRequest,
  UpdateApprovalRouteRequest,
  LintResponse
} from '../../services/approvalRouteVersionService'

interface ApprovalRouteDetailPageProps {
  approvalRouteVersionId?: string
  onNavigate: (page: string, data?: any) => void
}

type TabType = 'route-builder' | 'import-export' | 'lint'

interface RouteStep {
  step: number
  approverId: string
  label?: string
}

interface RouteDefinition {
  [routeKey: string]: RouteStep[]
}

export function ApprovalRouteDetailPage({ approvalRouteVersionId, onNavigate }: ApprovalRouteDetailPageProps) {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [approvalRoute, setApprovalRoute] = useState<ApprovalRouteDetail | null>(null)
  const [activeTab, setActiveTab] = useState<TabType>('route-builder')
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false)

  // Form data
  const [routeName, setRouteName] = useState('')
  const [description, setDescription] = useState('')
  const [routeDefinition, setRouteDefinition] = useState<RouteDefinition>({})
  const [selectedRouteKey, setSelectedRouteKey] = useState<string | null>(null)

  // Lint results
  const [lintResult, setLintResult] = useState<LintResponse | null>(null)
  const [lintLoading, setLintLoading] = useState(false)

  // Import/Export
  const [importJson, setImportJson] = useState('')

  // User search
  const [userSearchQuery, setUserSearchQuery] = useState('')
  const [userSearchResults, setUserSearchResults] = useState<UserSearchResult[]>([])
  const [searchingUsers, setSearchingUsers] = useState(false)
  const [focusedStepIndex, setFocusedStepIndex] = useState<number | null>(null)
  const [focusedRouteKey, setFocusedRouteKey] = useState<string | null>(null)
  const [userIdToNameMap, setUserIdToNameMap] = useState<{[userId: string]: string}>({})

  // Refs
  const routeNameInputRef = useRef<HTMLInputElement>(null)

  const isNew = !approvalRouteVersionId
  const isDraft = !approvalRoute || approvalRoute.status === 'DRAFT'

  useEffect(() => {
    if (approvalRouteVersionId) {
      loadApprovalRoute()
    } else {
      // 新規作成の場合、デフォルトでMAINルートを作成
      setRouteDefinition({ MAIN: [] })
      setSelectedRouteKey('MAIN')
    }
  }, [approvalRouteVersionId])

  const loadApprovalRoute = async () => {
    if (!approvalRouteVersionId) return

    try {
      setLoading(true)
      setError(null)
      const data = await ApprovalRouteVersionService.getById(approvalRouteVersionId)
      setApprovalRoute(data)
      setRouteName(data.routeName)
      setDescription(data.description || '')

      // Parse routes JSON
      const routes = typeof data.routes === 'string' ? JSON.parse(data.routes) : (data.routes || {})
      setRouteDefinition(routes)

      // Select first route key
      const firstKey = Object.keys(routes)[0]
      if (firstKey) {
        setSelectedRouteKey(firstKey)
      }
    } catch (err) {
      console.error('Failed to load approval route:', err)
      setError('承認ルートの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!routeName.trim()) {
      if (routeNameInputRef.current) {
        routeNameInputRef.current.setCustomValidity('ルート名を入力してください')
        routeNameInputRef.current.reportValidity()
      }
      return
    }

    if (Object.keys(routeDefinition).length === 0) {
      setError('少なくとも1つのルートキーが必要です')
      return
    }

    try {
      setSaving(true)
      setError(null)

      const routesJson = JSON.stringify(routeDefinition)

      if (isNew) {
        const request: CreateApprovalRouteRequest = {
          routeName,
          routes: routesJson,
          description: description || undefined
        }
        const created = await ApprovalRouteVersionService.create(request)
        // 作成後、詳細ページへ遷移
        onNavigate('approval-route-detail', { approvalRouteVersionId: created.approvalRouteVersionId })
        alert('承認ルートを作成しました')
      } else if (approvalRoute) {
        const request: UpdateApprovalRouteRequest = {
          routeName,
          routes: routesJson,
          description: description || undefined
        }
        await ApprovalRouteVersionService.update(approvalRoute.approvalRouteVersionId, request)
        await loadApprovalRoute()
        alert('承認ルートを更新しました')
      }
    } catch (err) {
      console.error('Save failed:', err)
      setError(isNew ? '作成に失敗しました' : '更新に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  const handleLint = async () => {
    try {
      setLintLoading(true)
      setError(null)
      const routesJson = JSON.stringify(routeDefinition)
      // 新規作成時は現在のroutesを、既存の場合はIDを使用
      const result = approvalRoute
        ? await ApprovalRouteVersionService.lint(approvalRoute.approvalRouteVersionId)
        : await ApprovalRouteVersionService.lint({ routes: routesJson })
      setLintResult(result)
      setActiveTab('lint')
    } catch (err) {
      console.error('Lint failed:', err)
      setError('整合性チェックに失敗しました')
    } finally {
      setLintLoading(false)
    }
  }

  const handlePublish = async () => {
    if (!approvalRoute) return

    if (!window.confirm('この承認ルートを確定してもよろしいですか？確定後は編集できなくなります。')) {
      return
    }

    try {
      setSaving(true)
      setError(null)
      // 確定前に自動検証
      const routesJson = JSON.stringify(routeDefinition)
      const lintResult = await ApprovalRouteVersionService.lint({ routes: routesJson })
      if (!lintResult.pass) {
        const errorMsg = `検証エラー:\n${lintResult.errors.join('\n')}`
        setError(errorMsg)
        setSaving(false)
        return
      }
      await ApprovalRouteVersionService.publish(approvalRoute.approvalRouteVersionId)
      await loadApprovalRoute()
      alert('承認ルートを確定しました')
    } catch (err: any) {
      console.error('Publish failed:', err)
      setError('確定に失敗しました: ' + (err.message || ''))
    } finally {
      setSaving(false)
    }
  }

  const handleDuplicateClick = () => {
    setShowDuplicateConfirm(true)
  }

  const handleDuplicateConfirm = async () => {
    if (!approvalRoute) return

    try {
      setSaving(true)
      setError(null)
      setShowDuplicateConfirm(false)
      const duplicated = await ApprovalRouteVersionService.duplicate(approvalRoute.approvalRouteVersionId)
      // 複製後、新しい承認ルートの詳細ページへ遷移
      onNavigate('approval-route-detail', { approvalRouteVersionId: duplicated.approvalRouteVersionId })
    } catch (err) {
      console.error('Duplicate failed:', err)
      setError('複製に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  // Route key management
  const handleAddRouteKey = () => {
    const newKey = prompt('新しいルートキーを入力してください（例: ALTERNATIVE, EMERGENCY）')
    if (!newKey) return

    // Validate route key format
    if (!/^[A-Z0-9_]+$/.test(newKey)) {
      alert('ルートキーは大文字英数字とアンダースコアのみ使用できます')
      return
    }

    if (routeDefinition[newKey]) {
      alert('このルートキーは既に存在します')
      return
    }

    setRouteDefinition({ ...routeDefinition, [newKey]: [] })
    setSelectedRouteKey(newKey)
  }

  const handleRemoveRouteKey = (routeKey: string) => {
    if (Object.keys(routeDefinition).length === 1) {
      alert('少なくとも1つのルートキーが必要です')
      return
    }

    if (!window.confirm(`ルートキー「${routeKey}」を削除してもよろしいですか？`)) {
      return
    }

    const newDefinition = { ...routeDefinition }
    delete newDefinition[routeKey]
    setRouteDefinition(newDefinition)

    // Select another route key
    if (selectedRouteKey === routeKey) {
      setSelectedRouteKey(Object.keys(newDefinition)[0] || null)
    }
  }

  // Step management
  const handleAddStep = () => {
    if (!selectedRouteKey) return

    const currentSteps = routeDefinition[selectedRouteKey] || []
    const nextStepNumber = currentSteps.length + 1

    setRouteDefinition({
      ...routeDefinition,
      [selectedRouteKey]: [
        ...currentSteps,
        { step: nextStepNumber, approverId: '', label: '' }
      ]
    })
  }

  const handleRemoveStep = (stepIndex: number) => {
    if (!selectedRouteKey) return

    if (!window.confirm('このステップを削除してもよろしいですか？')) {
      return
    }

    const currentSteps = routeDefinition[selectedRouteKey] || []
    const newSteps = currentSteps.filter((_, i) => i !== stepIndex)
    // Renumber steps
    newSteps.forEach((step, i) => {
      step.step = i + 1
    })

    setRouteDefinition({
      ...routeDefinition,
      [selectedRouteKey]: newSteps
    })
  }

  const handleStepChange = (stepIndex: number, field: keyof RouteStep, value: any) => {
    if (!selectedRouteKey) return

    const currentSteps = [...(routeDefinition[selectedRouteKey] || [])]
    currentSteps[stepIndex] = { ...currentSteps[stepIndex], [field]: value }

    setRouteDefinition({
      ...routeDefinition,
      [selectedRouteKey]: currentSteps
    })
  }

  // User search with debounce
  useEffect(() => {
    if (!userSearchQuery || userSearchQuery.length < 2) {
      setUserSearchResults([])
      return
    }

    const timer = setTimeout(async () => {
      try {
        setSearchingUsers(true)
        const users = await UserService.search({ keyword: userSearchQuery })
        setUserSearchResults(users)
      } catch (err) {
        console.error('User search failed:', err)
        setUserSearchResults([])
      } finally {
        setSearchingUsers(false)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [userSearchQuery])

  // Load user names for existing approver IDs
  useEffect(() => {
    const loadUserNames = async () => {
      const approverIds = new Set<string>()

      // Collect all approver IDs from route definition
      Object.values(routeDefinition).forEach(steps => {
        steps.forEach(step => {
          if (step.approverId && !userIdToNameMap[step.approverId]) {
            approverIds.add(step.approverId)
          }
        })
      })

      if (approverIds.size === 0) return

      try {
        // Fetch user info for all approver IDs
        const userPromises = Array.from(approverIds).map(userId =>
          UserService.search({ keyword: userId })
        )
        const results = await Promise.all(userPromises)

        const newMap: {[userId: string]: string} = { ...userIdToNameMap }
        results.forEach((users, index) => {
          const userId = Array.from(approverIds)[index]
          const user = users.find(u => u.userId === userId)
          if (user) {
            newMap[userId] = user.userName
          }
        })

        setUserIdToNameMap(newMap)
      } catch (err) {
        console.error('Failed to load user names:', err)
      }
    }

    loadUserNames()
  }, [routeDefinition])

  // Import/Export handlers
  const handleDownloadJson = () => {
    const exportData = routeDefinition
    const jsonString = JSON.stringify(exportData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)

    const date = new Date().toISOString().split('T')[0].replace(/-/g, '')
    const filename = `approval-route-${approvalRoute?.approvalRouteVersionId || 'new'}-${date}.json`

    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      const text = await file.text()
      const data = JSON.parse(text)

      // Basic validation
      if (typeof data !== 'object' || Array.isArray(data)) {
        setError('インポートファイルの形式が正しくありません: ルート定義オブジェクトが必要です')
        return
      }

      setImportJson(JSON.stringify(data, null, 2))
      setError(null)
    } catch (err) {
      console.error('Failed to parse import file:', err)
      setError('インポートファイルのJSON解析に失敗しました')
    }
  }

  const handleApplyImport = async () => {
    if (!importJson) return

    try {
      const parsed = JSON.parse(importJson)
      // インポート適用前に自動検証
      const lintResult = await ApprovalRouteVersionService.lint({ routes: JSON.stringify(parsed) })
      if (!lintResult.pass) {
        const errorMsg = `インポートファイルの検証エラー:\n${lintResult.errors.join('\n')}`
        setError(errorMsg)
        return
      }
      setRouteDefinition(parsed)
      setImportJson('')
      setActiveTab('route-builder')
      setError(null)

      // Select first route key
      const firstKey = Object.keys(parsed)[0]
      if (firstKey) {
        setSelectedRouteKey(firstKey)
      }
    } catch (err) {
      setError('JSONの解析に失敗しました')
    }
  }

  const getRouteKeyLabel = (routeKey: string) => {
    if (routeKey === 'MAIN') return '通常ルート'
    return routeKey
  }

  const getStatusBadge = (status: string) => {
    const statusMap: { [key: string]: { label: string; bgColor: string; textColor: string } } = {
      DRAFT: { label: '編集中', bgColor: '#f3f4f6', textColor: '#374151' },
      PUBLISHED: { label: '確定', bgColor: '#d1fae5', textColor: '#065f46' },
      ARCHIVED: { label: '廃止', bgColor: '#fef3c7', textColor: '#92400e' }
    }
    const config = statusMap[status] || { label: status, bgColor: '#f3f4f6', textColor: '#374151' }
    return (
      <span style={{
        padding: '0.25rem 0.5rem',
        fontSize: '0.75rem',
        fontWeight: '500',
        borderRadius: '0.25rem',
        backgroundColor: config.bgColor,
        color: config.textColor
      }}>
        {config.label}
      </span>
    )
  }

  if (loading) {
    return <div className="p-8 text-center text-gray-500">読み込み中...</div>
  }

  return (
    <div className="px-8 py-6">
      <button
        onClick={() => onNavigate('approval-routes-list')}
        className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
        style={{ marginBottom: '1.5rem' }}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        一覧に戻る
      </button>

      {/* Header */}
      <div style={{
        borderBottom: '2px solid #e5e7eb',
        paddingBottom: '0.75rem',
        marginBottom: '2rem'
      }}>
        <h1 className="page-title">
          {isNew ? '承認ルート新規作成' : '承認ルート詳細'}
        </h1>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #e5e7eb',
          color: '#b91c1c',
          padding: '1rem',
          borderRadius: '0.5rem',
          marginBottom: '1.5rem'
        }}>
          {error}
        </div>
      )}

      {/* アクション */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">アクション</h2>
        <div className="flex gap-4">
          {!isNew && !isDraft && (
            <button
              onClick={handleDuplicateClick}
              className="btn btn-secondary"
              disabled={saving}
              style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
            >
              複製して作成
            </button>
          )}
          {isDraft && (
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                fontWeight: 500,
                border: '1px solid #2563eb',
                borderRadius: '0.375rem',
                color: 'white',
                backgroundColor: '#2563eb',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.5 : 1,
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => {
                if (!saving) {
                  e.currentTarget.style.backgroundColor = '#1d4ed8'
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#2563eb'
              }}
            >
              {saving ? '保存中...' : '保存'}
            </button>
          )}
          <button
            onClick={handleLint}
            disabled={lintLoading}
            className="btn btn-secondary"
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              opacity: lintLoading ? 0.5 : 1,
              cursor: lintLoading ? 'not-allowed' : 'pointer'
            }}
          >
            {lintLoading ? '整合性チェック中...' : '整合性チェック'}
          </button>
          {!isNew && isDraft && (
            <button
              onClick={handlePublish}
              disabled={saving}
              className="btn btn-primary"
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                opacity: saving ? 0.5 : 1,
                cursor: saving ? 'not-allowed' : 'pointer'
              }}
            >
              {saving ? '確定中...' : '確定'}
            </button>
          )}
        </div>
      </div>

      {/* Basic Information */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.3rem', marginBottom: '1.5rem' }}>
          <h2 className="text-lg font-semibold text-gray-900">基本情報</h2>
          {approvalRoute && getStatusBadge(approvalRoute.status)}
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              承認ルートID
            </label>
            <input
              type="text"
              value={approvalRoute?.approvalRouteVersionId || '（保存時に自動生成）'}
              readOnly
              disabled
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                backgroundColor: '#f9fafb',
                color: '#6b7280',
                cursor: 'not-allowed'
              }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ルート名 <span className="text-red-600">*</span>
            </label>
            <input
              ref={routeNameInputRef}
              type="text"
              value={routeName}
              onChange={(e) => {
                setRouteName(e.target.value)
                if (routeNameInputRef.current) {
                  routeNameInputRef.current.setCustomValidity('')
                }
              }}
              disabled={!isDraft}
              placeholder="例: 標準承認ルート"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              説明（任意）
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={!isDraft}
              placeholder="例: 一般的な申請用の承認ルート"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            />
          </div>

          {approvalRoute && (
            <div style={{ display: 'flex', gap: '2rem', fontSize: '0.875rem' }}>
              <div>
                <span style={{ color: '#6b7280' }}>作成日時:</span>{' '}
                <span style={{ color: '#111827' }}>{new Date(approvalRoute.createdAt).toLocaleString('ja-JP')}</span>
              </div>
              <div>
                <span style={{ color: '#6b7280' }}>更新日時:</span>{' '}
                <span style={{ color: '#111827' }}>{new Date(approvalRoute.updatedAt).toLocaleString('ja-JP')}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        marginBottom: '1.5rem'
      }}>
        <div style={{ borderBottom: '1px solid #e5e7eb' }}>
          <nav style={{ display: 'flex', marginBottom: '-1px' }}>
          {[
            { id: 'route-builder' as TabType, label: 'ルート定義' },
            { id: 'import-export' as TabType, label: 'インポート／エクスポート' },
            { id: 'lint' as TabType, label: '整合性チェック' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '0.75rem 1.5rem',
                fontSize: '0.875rem',
                fontWeight: 500,
                borderBottom: activeTab === tab.id ? '2px solid #3b82f6' : '2px solid transparent',
                color: activeTab === tab.id ? '#3b82f6' : '#6b7280',
                backgroundColor: activeTab === tab.id ? '#f9fafb' : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.color = '#374151'
                  e.currentTarget.style.borderBottomColor = '#d1d5db'
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.color = '#6b7280'
                  e.currentTarget.style.borderBottomColor = 'transparent'
                }
              }}
            >
              {tab.label}
            </button>
          ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div style={{ padding: '1.5rem' }}>
      {/* Tab Content: Route Builder */}
      {activeTab === 'route-builder' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '1.5rem' }}>
            {/* Left: Route Keys */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '600' }}>ルート一覧</h3>
                {isDraft && (
                  <button
                    onClick={handleAddRouteKey}
                    className="btn btn-primary"
                    style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                  >
                    + 追加
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {Object.keys(routeDefinition).map(routeKey => (
                  <div
                    key={routeKey}
                    onClick={() => setSelectedRouteKey(routeKey)}
                    style={{
                      padding: '0.75rem',
                      backgroundColor: selectedRouteKey === routeKey ? '#eff6ff' : 'white',
                      border: `1px solid ${selectedRouteKey === routeKey ? '#3b82f6' : '#e5e7eb'}`,
                      borderRadius: '0.375rem',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <span style={{ fontWeight: '500', fontSize: '0.875rem' }}>{getRouteKeyLabel(routeKey)}</span>
                    {isDraft && Object.keys(routeDefinition).length > 1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRemoveRouteKey(routeKey)
                        }}
                        style={{
                          padding: '0.125rem 0.25rem',
                          fontSize: '0.75rem',
                          color: '#ef4444',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Steps Table */}
            <div>
              {selectedRouteKey ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h3 style={{ fontSize: '1rem', fontWeight: '600' }}>ステップ定義: {getRouteKeyLabel(selectedRouteKey)}</h3>
                    {isDraft && (
                      <button
                        onClick={handleAddStep}
                        className="btn btn-primary"
                        style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                      >
                        + ステップ追加
                      </button>
                    )}
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ backgroundColor: '#f8fafc' }}>
                      <tr>
                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', border: '1px solid #e5e7eb' }}>Step</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', border: '1px solid #e5e7eb' }}>承認者</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', border: '1px solid #e5e7eb' }}>ラベル</th>
                        {isDraft && (
                          <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', border: '1px solid #e5e7eb', width: '80px' }}>操作</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {(routeDefinition[selectedRouteKey] || []).map((step, index) => (
                        <tr key={index}>
                          <td style={{ padding: '0.75rem', border: '1px solid #e5e7eb' }}>
                            {step.step}
                          </td>
                          <td style={{ padding: '0.75rem', border: '1px solid #e5e7eb', position: 'relative' }}>
                            <input
                              type="text"
                              value={userIdToNameMap[step.approverId] || step.approverId}
                              onChange={(e) => {
                                handleStepChange(index, 'approverId', e.target.value)
                                setUserSearchQuery(e.target.value)
                              }}
                              onFocus={() => {
                                setFocusedStepIndex(index)
                                setFocusedRouteKey(selectedRouteKey)
                                setUserSearchQuery(step.approverId)
                              }}
                              onBlur={() => {
                                // Delay to allow clicking on dropdown items
                                setTimeout(() => {
                                  setFocusedStepIndex(null)
                                  setFocusedRouteKey(null)
                                }, 200)
                              }}
                              disabled={!isDraft}
                              placeholder="ユーザーIDまたは名前で検索"
                              style={{
                                width: '100%',
                                padding: '0.375rem 0.5rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '0.25rem',
                                fontSize: '0.875rem'
                              }}
                            />
                            {/* User search dropdown */}
                            {focusedStepIndex === index &&
                             focusedRouteKey === selectedRouteKey &&
                             userSearchResults.length > 0 && (
                              <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: '0.75rem',
                                right: '0.75rem',
                                backgroundColor: 'white',
                                border: '1px solid #d1d5db',
                                borderRadius: '0.25rem',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                zIndex: 1000,
                                maxHeight: '200px',
                                overflowY: 'auto',
                                marginTop: '2px'
                              }}>
                                {userSearchResults.map((user) => (
                                  <div
                                    key={user.userId}
                                    onClick={() => {
                                      handleStepChange(index, 'approverId', user.userId)
                                      setUserIdToNameMap((prev) => ({
                                        ...prev,
                                        [user.userId]: user.userName
                                      }))
                                      setUserSearchResults([])
                                      setUserSearchQuery('')
                                      setFocusedStepIndex(null)
                                      setFocusedRouteKey(null)
                                    }}
                                    style={{
                                      padding: '0.5rem',
                                      cursor: 'pointer',
                                      fontSize: '0.875rem',
                                      borderBottom: '1px solid #f3f4f6'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#f3f4f6'
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = 'white'
                                    }}
                                  >
                                    <div style={{ fontWeight: '500' }}>{user.userName}</div>
                                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                      ID: {user.userId} | ロール: {user.role}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '0.75rem', border: '1px solid #e5e7eb' }}>
                            <input
                              type="text"
                              value={step.label || ''}
                              onChange={(e) => handleStepChange(index, 'label', e.target.value)}
                              disabled={!isDraft}
                              placeholder="例: 部長承認"
                              style={{
                                width: '100%',
                                padding: '0.375rem 0.5rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '0.25rem',
                                fontSize: '0.875rem'
                              }}
                            />
                          </td>
                          {isDraft && (
                            <td style={{ padding: '0.75rem', border: '1px solid #e5e7eb' }}>
                              <button
                                onClick={() => handleRemoveStep(index)}
                                style={{
                                  padding: '0.25rem 0.5rem',
                                  fontSize: '0.75rem',
                                  color: '#ef4444',
                                  background: 'none',
                                  border: '1px solid #ef4444',
                                  borderRadius: '0.25rem',
                                  cursor: 'pointer'
                                }}
                              >
                                削除
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {routeDefinition[selectedRouteKey]?.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                      ステップがありません。「+ ステップ追加」ボタンから追加してください。
                    </div>
                  )}
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                  左からルートキーを選択してください
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tab Content: Import/Export */}
      {activeTab === 'import-export' && (
        <div className="space-y-6">
          {/* Export Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">エクスポート</h3>
            <p className="text-sm text-gray-600 mb-4">
              現在の承認ルート定義をJSONファイルとしてダウンロードします。
            </p>
            <button
              onClick={handleDownloadJson}
              className="btn btn-primary"
            >
              JSONファイルをダウンロード
            </button>
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid #e5e7eb' }} />

          {/* Import Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">インポート</h3>
            <p className="text-sm text-gray-600 mb-4">
              JSONファイルを選択して承認ルート定義をインポートします。
            </p>

            {/* File Input */}
            <div className="mb-4">
              <input
                type="file"
                accept="application/json,.json"
                onChange={handleFileSelect}
                style={{
                  display: 'block',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem'
                }}
              />
            </div>

            {/* Preview */}
            {importJson && (
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">プレビュー</h4>
                  <div style={{
                    backgroundColor: '#1f2937',
                    color: '#f3f4f6',
                    padding: '1rem',
                    borderRadius: '0.375rem',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    overflow: 'auto',
                    maxHeight: '300px'
                  }}>
                    <pre>{importJson}</pre>
                  </div>
                </div>

                {/* Apply Button */}
                {isDraft && (
                  <div className="flex justify-end">
                    <button
                      onClick={handleApplyImport}
                      className="btn btn-primary"
                    >
                      適用する
                    </button>
                  </div>
                )}

                {!isDraft && (
                  <div style={{
                    padding: '0.75rem',
                    backgroundColor: '#fef3c7',
                    border: '1px solid #fbbf24',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    color: '#92400e'
                  }}>
                    確定済みの承認ルートは編集できません。
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab Content: Lint */}
      {activeTab === 'lint' && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">整合性チェック結果</h3>
          {!lintResult ? (
            <div className="text-center text-gray-500 py-8">
              整合性チェックを実行してください
            </div>
          ) : (
            <div className="space-y-4">
              {/* 検証項目の説明 */}
              {lintResult.pass && (
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#f9fafb',
                  borderRadius: '0.375rem',
                  border: '1px solid #e5e7eb'
                }}>
                  <h4 style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '0.75rem'
                  }}>
                    チェック完了：以下の項目を検証しました
                    {lintResult.warnings && lintResult.warnings.length > 0 ? (
                      <span style={{ color: '#f59e0b', marginLeft: '0.5rem' }}>
                        （警告：{lintResult.warnings.length}件）
                      </span>
                    ) : (
                      <span style={{ color: '#6b7280', marginLeft: '0.5rem' }}>
                        （警告・エラーなし）
                      </span>
                    )}
                  </h4>
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem'
                  }}>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#374151' }}>
                      <span style={{ display: 'inline-block', width: '1rem', textAlign: 'center', color: '#10b981', flexShrink: 0 }}>✓</span>
                      <span>承認ルートが定義されているか</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#374151' }}>
                      <span style={{ display: 'inline-block', width: '1rem', textAlign: 'center', color: '#10b981', flexShrink: 0 }}>✓</span>
                      <span>ルートキーが正しい形式か（大文字英数字とアンダースコア）</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#374151' }}>
                      <span style={{ display: 'inline-block', width: '1rem', textAlign: 'center', color: '#10b981', flexShrink: 0 }}>✓</span>
                      <span>各ルートに少なくとも1つのステップがあるか</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#374151' }}>
                      <span style={{ display: 'inline-block', width: '1rem', textAlign: 'center', color: '#10b981', flexShrink: 0 }}>✓</span>
                      <span>ステップ番号が1から連番になっているか</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#374151' }}>
                      <span style={{ display: 'inline-block', width: '1rem', textAlign: 'center', color: '#10b981', flexShrink: 0 }}>✓</span>
                      <span>ステップに重複がないか</span>
                    </li>
                    <li style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#374151' }}>
                        <span style={{
                          display: 'inline-block',
                          width: '1rem',
                          textAlign: 'center',
                          color: lintResult.warnings && lintResult.warnings.length > 0 ? '#f59e0b' : '#10b981',
                          flexShrink: 0
                        }}>
                          {lintResult.warnings && lintResult.warnings.length > 0 ? '⚠' : '✓'}
                        </span>
                        <span>承認者IDに問題がないか</span>
                      </div>
                      {lintResult.warnings && lintResult.warnings.length > 0 && (
                        <div style={{ marginLeft: '1.5rem', fontSize: '0.75rem', color: '#92400e' }}>
                          {lintResult.warnings.map((warning, index) => (
                            <div key={index}>{warning}</div>
                          ))}
                        </div>
                      )}
                    </li>
                  </ul>
                </div>
              )}

              {/* Errors */}
              {!lintResult.pass && lintResult.errors && lintResult.errors.length > 0 && (
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#fee2e2',
                  borderRadius: '0.375rem',
                  border: '1px solid #fecaca'
                }}>
                  <h4 style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#dc2626',
                    marginBottom: '0.75rem'
                  }}>
                    検証失敗：以下のエラーを修正してください（エラー：{lintResult.errors.length}件）
                  </h4>
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem'
                  }}>
                    {lintResult.errors.map((error, index) => (
                      <li key={index} style={{ fontSize: '0.875rem', color: '#991b1b' }}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 複製確認ダイアログ */}
      {showDuplicateConfirm && approvalRoute && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-4">複製確認</h2>
            <p className="text-gray-700 mb-2">
              以下の承認ルートを複製して新しい下書きを作成しますか？
            </p>
            <div className="bg-gray-50 p-3 rounded mb-4">
              <div className="text-sm">
                <div><strong>承認ルート名:</strong> {approvalRoute.routeName}</div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button
                onClick={() => setShowDuplicateConfirm(false)}
                disabled={saving}
                className="btn btn-secondary"
              >
                キャンセル
              </button>
              <button
                onClick={handleDuplicateConfirm}
                disabled={saving}
                className="btn btn-primary"
                style={saving ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                {saving ? '複製中...' : '複製して作成'}
              </button>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  )
}
